import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, ShieldCheck, BadgeCheck, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Hero() {
  return (
    <section data-testid="home-hero" className="relative isolate flex min-h-[92vh] items-end overflow-hidden">
      {/* Background image */}
      <img
        src="https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=2600&q=80"
        alt="Premium Grade-A office building in Hyderabad"
        className="absolute inset-0 -z-20 h-full w-full object-cover"
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#144726]/85 via-[#144726]/65 to-[#144726]/85" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(80%_60%_at_50%_30%,rgba(255,255,255,0.06),transparent)]" />

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C6A35B]">Your Growth, Our Commitment</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-7xl font-semibold leading-[1.05] tracking-tight text-white">
              Unlock the Full Potential of Your Commercial Real Estate Assets.
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-white/80">
              Cespedis delivers strategic commercial leasing and professional property management solutions that increase occupancy, maximize rental income, and create long-term asset value.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link to="/contact" data-testid="hero-schedule-consultation-button">
                <Button size="lg" className="h-12 rounded-lg bg-[#C6A35B] px-6 text-[#144726] font-semibold hover:bg-[#B8923E]">
                  Schedule a Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/properties" data-testid="hero-view-properties-button">
                <Button size="lg" variant="outline" className="h-12 rounded-lg border-white/30 bg-white/5 px-6 text-white hover:bg-white/10 hover:text-white">
                  View Available Properties
                </Button>
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-white/70">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#C6A35B]" /> Institutional-grade process</div>
              <div className="hidden sm:block h-4 w-px bg-white/15" />
              <div className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-[#C6A35B]" /> Conflict-free representation</div>
              <div className="hidden sm:block h-4 w-px bg-white/15" />
              <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#C6A35B]" /> Benchmark-led pricing</div>
            </div>
          </div>

          {/* Right stack card */}
          <div className="lg:col-span-4">
            <div className="rounded-2xl border border-white/15 bg-white/[0.06] p-6 backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C6A35B]">At a glance</p>
              <div className="mt-5 grid grid-cols-2 gap-5">
                <div>
                  <p className="font-display text-3xl font-semibold text-white">1M+</p>
                  <p className="text-xs text-white/60 mt-1">Sq Ft Leasing Potential</p>
                </div>
                <div>
                  <p className="font-display text-3xl font-semibold text-white">100+</p>
                  <p className="text-xs text-white/60 mt-1">Tenant Connections</p>
                </div>
                <div>
                  <p className="font-display text-3xl font-semibold text-white">50+</p>
                  <p className="text-xs text-white/60 mt-1">Assets Under Mandate</p>
                </div>
                <div>
                  <p className="font-display text-3xl font-semibold text-white">95%</p>
                  <p className="text-xs text-white/60 mt-1">Occupancy Focus</p>
                </div>
              </div>
              <div className="mt-6 rounded-lg border border-white/10 bg-[#144726]/40 p-4">
                <p className="text-sm font-semibold text-white">Speak with a leasing advisor</p>
                <p className="mt-1 text-xs text-white/70">Tailored guidance for landlords, developers, and occupiers in Hyderabad.</p>
                <Link to="/contact" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#C6A35B] hover:text-white transition-colors link-gold">Talk to us <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <a href="#about" aria-label="Scroll down" className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center text-[#C6A35B] text-xs tracking-[0.3em] uppercase">
        Scroll <ChevronDown className="mt-1 h-4 w-4 animate-bounce" />
      </a>
    </section>
  );
}
